import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { createLicense, localStorageStore, type License, type LicenseStatus } from '../../core';
import { ALLOW_LOCAL_LICENSE, LICENSE_API_URL } from '../../config/license';
import { createRemoteValidator, localValidator } from './remoteValidator';
import { LicenseContext, type LicenseValue } from './context';

/**
 * The production License. The local validator seeds the cache synchronously and
 * handles the offline/dev/e2e path; on a real production build it rejects every
 * local key (cue-forge-sdv.1), so Pro can only be granted by the remote
 * (Lemon Squeezy) validator once the proxy Worker URL is configured. Empty URL ⇒
 * local-only (dev/e2e/offline); the default build and tests are unchanged.
 */
function defaultLicense(): License {
  return createLicense(
    localStorageStore,
    localValidator(ALLOW_LOCAL_LICENSE),
    LICENSE_API_URL ? { remote: createRemoteValidator(LICENSE_API_URL) } : {},
  );
}

/**
 * Pro-license provider (spec §7.5, SP2). Wraps the framework-free {@link License}
 * (localStorage-backed by default, so the cached status survives reloads) and
 * exposes its state reactively. Gating stays **render-safe and synchronous**:
 * `isPro` reads the cached, grace-adjusted status. The provider re-validates
 * asynchronously on mount and on an interval so a subscription that expires —
 * or is renewed/cancelled remotely — re-locks or unlocks without a reload; when
 * offline the cached status (within its grace window) is kept. The `license`
 * prop is injectable for tests.
 */

/** Re-validate / re-evaluate the license this often (remote refresh + offline grace). */
export const LICENSE_REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 h

/** Gating-relevant equality — ignores lastValidatedAt so a no-op refresh never re-renders. */
function sameGating(a: LicenseStatus, b: LicenseStatus): boolean {
  return a.valid === b.valid && a.state === b.state && a.expiresAt === b.expiresAt;
}

export function LicenseProvider({
  children,
  license = defaultLicense(),
}: {
  children: ReactNode;
  license?: License;
}) {
  const [status, setStatus] = useState<LicenseStatus>(() => license.current());

  const applyStatus = useCallback((next: LicenseStatus) => {
    setStatus((prev) => (sameGating(prev, next) ? prev : next));
  }, []);

  // Re-validate on mount, then periodically. On failure (offline) fall back to
  // the cached status re-evaluated for grace, so an expired license re-locks once
  // grace lapses even without a network round-trip.
  useEffect(() => {
    let active = true;
    const sync = () => {
      license
        .refresh()
        .then((s) => {
          if (active) applyStatus(s);
        })
        .catch(() => {
          if (active) applyStatus(license.current());
        });
    };
    sync();
    const id = setInterval(sync, LICENSE_REFRESH_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [license, applyStatus]);

  const activate = useCallback(
    async (key: string) => {
      // activateRemote validates real keys against the proxy and persists them;
      // stub / DEV keys resolve immediately with no network call.
      const result = await license.activateRemote(key);
      applyStatus(license.current());
      return result;
    },
    [license, applyStatus],
  );

  const deactivate = useCallback(() => {
    license.deactivate();
    applyStatus(license.current());
  }, [license, applyStatus]);

  const value = useMemo<LicenseValue>(
    () => ({ isPro: status.valid, status, activate, deactivate }),
    [status, activate, deactivate],
  );

  return <LicenseContext.Provider value={value}>{children}</LicenseContext.Provider>;
}
